/**
 * Core Contact Form Section Component
 * 
 * Base contact form component that can be themed and customized.
 * Provides a flexible form structure for property enquiries.
 */

'use client';

import { useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { analyticsEvents, setEnhancedConversionData } from '@/components/analytics/GoogleAnalytics';
import { collectAttribution } from '@/lib/attribution';
import { TurnstileWidget } from '@/components/ui/TurnstileWidget';

// ============================================================================
// ANIMATED SUCCESS COMPONENT
// ============================================================================

interface ConfettiPiece {
  id: number;
  x: number;
  delay: number;
  duration: number;
  color: string;
  size: number;
  rotation: number;
}

interface SuccessAnimationProps {
  message: string;
  onReset?: () => void;
}

function SuccessAnimation({ message, onReset }: SuccessAnimationProps) {
  const [confetti, setConfetti] = useState<ConfettiPiece[]>([]);
  const [showContent, setShowContent] = useState(false);

  // Allys brand colors for confetti
  const confettiColors = [
    '#243462', // Navy
    '#BBA339', // Gold
    '#E4CE4A', // Light gold
    '#040813', // Dark
    '#6B7280', // Gray
  ];

  const generateConfetti = useCallback(() => {
    const pieces: ConfettiPiece[] = [];
    for (let i = 0; i < 50; i++) {
      pieces.push({
        id: i,
        x: Math.random() * 100,
        delay: Math.random() * 0.5,
        duration: 2 + Math.random() * 2,
        color: confettiColors[Math.floor(Math.random() * confettiColors.length)] ?? '#243462',
        size: 6 + Math.random() * 8,
        rotation: Math.random() * 360,
      });
    }
    return pieces;
  }, []);

  useEffect(() => {
    setConfetti(generateConfetti());
    // Stagger content appearance
    const timer = setTimeout(() => setShowContent(true), 300);
    return () => clearTimeout(timer);
  }, [generateConfetti]);

  return (
    <div className="success-animation-container">
      {/* Confetti Layer */}
      <div className="confetti-container">
        {confetti.map((piece) => (
          <div
            key={piece.id}
            className="confetti-piece"
            style={{
              left: `${piece.x}%`,
              animationDelay: `${piece.delay}s`,
              animationDuration: `${piece.duration}s`,
              backgroundColor: piece.color,
              width: `${piece.size}px`,
              height: `${piece.size * 0.6}px`,
              transform: `rotate(${piece.rotation}deg)`,
            }}
          />
        ))}
      </div>

      {/* Success Content */}
      <div className={`success-content ${showContent ? 'visible' : ''}`}>
        {/* Animated Checkmark Circle */}
        <div className="checkmark-container">
          <svg className="checkmark-circle" viewBox="0 0 100 100">
            {/* Background circle pulse */}
            <circle className="pulse-ring" cx="50" cy="50" r="45" />
            {/* Main circle */}
            <circle className="circle-bg" cx="50" cy="50" r="40" />
            <circle className="circle-progress" cx="50" cy="50" r="40" />
            {/* Checkmark */}
            <path className="checkmark-path" d="M30 50 L45 65 L70 35" />
          </svg>
          
          {/* Sparkles around the checkmark */}
          <div className="sparkle sparkle-1">✦</div>
          <div className="sparkle sparkle-2">✦</div>
          <div className="sparkle sparkle-3">✦</div>
          <div className="sparkle sparkle-4">✦</div>
        </div>

        {/* Success Text */}
        <div className="success-text-container">
          <h3 className="success-title">Message Sent!</h3>
          <p className="success-message">{message}</p>
          <p className="success-subtext">Our team will respond within 24 hours</p>
        </div>

        {/* Send Another Button */}
        {onReset && (
          <button onClick={onReset} className="send-another-btn">
            Send Another Message
          </button>
        )}
      </div>

      <style jsx>{`
        .success-animation-container {
          position: relative;
          min-height: 400px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          padding: 40px 20px;
        }

        /* Confetti Styles */
        .confetti-container {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          overflow: hidden;
        }

        .confetti-piece {
          position: absolute;
          top: -20px;
          border-radius: 2px;
          animation: confetti-fall linear forwards;
          opacity: 0;
        }

        @keyframes confetti-fall {
          0% {
            opacity: 1;
            transform: translateY(0) rotate(0deg) scale(1);
          }
          25% {
            opacity: 1;
          }
          100% {
            opacity: 0;
            transform: translateY(500px) rotate(720deg) scale(0.5);
          }
        }

        /* Success Content */
        .success-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          opacity: 0;
          transform: translateY(20px) scale(0.95);
          transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .success-content.visible {
          opacity: 1;
          transform: translateY(0) scale(1);
        }

        /* Checkmark Container */
        .checkmark-container {
          position: relative;
          width: 120px;
          height: 120px;
          margin-bottom: 24px;
        }

        .checkmark-circle {
          width: 100%;
          height: 100%;
        }

        .pulse-ring {
          fill: none;
          stroke: #BBA339;
          stroke-width: 2;
          opacity: 0;
          transform-origin: center;
          animation: pulse-ring 2s ease-out infinite;
        }

        @keyframes pulse-ring {
          0% {
            opacity: 0.6;
            transform: scale(0.8);
          }
          100% {
            opacity: 0;
            transform: scale(1.3);
          }
        }

        .circle-bg {
          fill: #F8F9FA;
          stroke: #E5E7EB;
          stroke-width: 2;
        }

        .circle-progress {
          fill: none;
          stroke: #243462;
          stroke-width: 4;
          stroke-linecap: round;
          stroke-dasharray: 251.2;
          stroke-dashoffset: 251.2;
          transform: rotate(-90deg);
          transform-origin: center;
          animation: circle-draw 0.8s ease-out 0.2s forwards;
        }

        @keyframes circle-draw {
          to {
            stroke-dashoffset: 0;
          }
        }

        .checkmark-path {
          fill: none;
          stroke: #BBA339;
          stroke-width: 5;
          stroke-linecap: round;
          stroke-linejoin: round;
          stroke-dasharray: 60;
          stroke-dashoffset: 60;
          animation: checkmark-draw 0.5s ease-out 0.8s forwards;
        }

        @keyframes checkmark-draw {
          to {
            stroke-dashoffset: 0;
          }
        }

        /* Sparkles */
        .sparkle {
          position: absolute;
          font-size: 16px;
          color: #BBA339;
          opacity: 0;
          animation: sparkle-pop 0.6s ease-out forwards;
        }

        .sparkle-1 { top: 10px; left: 10px; animation-delay: 1s; }
        .sparkle-2 { top: 10px; right: 10px; animation-delay: 1.1s; }
        .sparkle-3 { bottom: 10px; left: 10px; animation-delay: 1.2s; }
        .sparkle-4 { bottom: 10px; right: 10px; animation-delay: 1.3s; }

        @keyframes sparkle-pop {
          0% {
            opacity: 0;
            transform: scale(0) rotate(0deg);
          }
          50% {
            opacity: 1;
            transform: scale(1.2) rotate(180deg);
          }
          100% {
            opacity: 0.8;
            transform: scale(1) rotate(360deg);
          }
        }

        /* Text Styles */
        .success-text-container {
          margin-bottom: 24px;
        }

        .success-title {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 28px;
          font-weight: 600;
          color: #243462;
          margin: 0 0 12px 0;
          letter-spacing: -0.5px;
        }

        .success-message {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 16px;
          color: #6B7280;
          margin: 0 0 8px 0;
          font-weight: 400;
        }

        .success-subtext {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 14px;
          color: #9CA3AF;
          margin: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .success-subtext::before {
          content: '⏱';
        }

        /* Send Another Button */
        .send-another-btn {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          background: transparent;
          color: #243462;
          border: 1px solid #E5E7EB;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          opacity: 0;
          animation: fade-in 0.4s ease-out 1.5s forwards;
        }

        .send-another-btn:hover {
          background: #F8F9FA;
          border-color: #243462;
        }

        @keyframes fade-in {
          to {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export interface ContactFormField {
  name: string;
  label: string;
  placeholder: string;
  type: 'text' | 'email' | 'tel' | 'select' | 'textarea';
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
}

export interface ContactFormSectionProps {
  title: string;
  subtitle?: string;
  fields: ContactFormField[];
  submitLabel: string;
  sendingLabel: string;
  successMessage: string;
  errorMessage: string;
  onSubmit?: (data: Record<string, string>) => Promise<void>;
  className?: string;
  renderField?: (
    field: ContactFormField,
    value: string,
    onChange: (value: string) => void,
    error?: string
  ) => ReactNode;
}

/**
 * Core Contact Form Section Component
 * Clean, flexible base that themes can extend
 */
export function ContactFormSection({
  title,
  subtitle,
  fields,
  submitLabel,
  sendingLabel,
  successMessage,
  errorMessage,
  onSubmit,
  className = '',
  renderField,
}: ContactFormSectionProps) {
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const handleChange = (fieldName: string, value: string) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    fields.forEach(field => {
      if (field.required && !formData[field.name]?.trim()) {
        newErrors[field.name] = `${field.label} is required`;
      }

      // Email validation
      if (field.type === 'email' && formData[field.name]) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const emailValue = formData[field.name];
        if (emailValue && !emailRegex.test(emailValue)) {
          newErrors[field.name] = 'Invalid email address';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      if (onSubmit) {
        await onSubmit(formData);
      } else {
        const attribution = collectAttribution();
        const currentLocale = typeof window !== 'undefined'
          ? window.location.pathname.split('/')[1] || 'en'
          : 'en';

        const response = await fetch('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...formData,
            locale: currentLocale,
            metadata: attribution,
            turnstileToken: turnstileToken || undefined,
          }),
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to send message');
        }
      }
      setSubmitStatus('success');
      setFormData({});

      // Set enhanced conversion data before firing conversion events
      setEnhancedConversionData({
        email: formData.email as string | undefined,
        phone: formData.phone as string | undefined,
        firstName: formData.firstName as string | undefined,
        lastName: formData.lastName as string | undefined,
      });

      analyticsEvents.formSubmit('contact_form', window.location.pathname);
      analyticsEvents.generateLead('contact_form', undefined, {
        email: formData.email as string | undefined,
        phone: formData.phone as string | undefined,
        firstName: formData.firstName as string | undefined,
        lastName: formData.lastName as string | undefined,
      });

      // Push to dataLayer for GTM-based triggers
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'form_submission',
          form_name: 'contact_form',
          form_location: window.location.pathname,
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setSubmitStatus('idle');
    setFormData({});
    setErrors({});
  };

  // Show success animation when form is submitted successfully
  if (submitStatus === 'success') {
    return (
      <section className={`contact-form-section ${className}`}>
        <SuccessAnimation 
          message={successMessage} 
          onReset={handleReset}
        />
      </section>
    );
  }

  return (
    <section className={`contact-form-section ${className}`}>
      <div className="contact-form-header">
        <h2 className="contact-form-title">{title}</h2>
        {subtitle && <p className="contact-form-subtitle">{subtitle}</p>}
      </div>

      <form onSubmit={handleSubmit} className="contact-form">
        {fields.map(field => {
          const value = formData[field.name] || '';
          const error = errors[field.name];

          if (renderField) {
            return (
              <div key={field.name} className="contact-form-field">
                {renderField(field, value, (newValue) => handleChange(field.name, newValue), error)}
              </div>
            );
          }

          // Default rendering
          return (
            <div key={field.name} className="contact-form-field">
              <label htmlFor={field.name} className="contact-form-label">
                {field.label}
                {field.required && <span className="contact-form-required">*</span>}
              </label>

              {field.type === 'textarea' ? (
                <textarea
                  id={field.name}
                  name={field.name}
                  value={value}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  placeholder={field.placeholder}
                  required={field.required}
                  rows={5}
                  className="contact-form-input contact-form-textarea"
                />
              ) : field.type === 'select' ? (
                <select
                  id={field.name}
                  name={field.name}
                  value={value}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  required={field.required}
                  className="contact-form-input contact-form-select"
                >
                  <option value="">{field.placeholder}</option>
                  {field.options?.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={field.type}
                  id={field.name}
                  name={field.name}
                  value={value}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  placeholder={field.placeholder}
                  required={field.required}
                  className="contact-form-input"
                />
              )}

              {error && <p className="contact-form-error">{error}</p>}
            </div>
          );
        })}

        <TurnstileWidget
          onVerify={handleTurnstileVerify}
          onExpire={handleTurnstileExpire}
          className="flex justify-center"
        />

        <button
          type="submit"
          disabled={isSubmitting}
          className="contact-form-submit"
        >
          {isSubmitting ? sendingLabel : submitLabel}
        </button>

        {submitStatus === 'error' && (
          <p className="contact-form-error-message">{errorMessage}</p>
        )}
      </form>
    </section>
  );
}

// Default export for backward compatibility
export default ContactFormSection;

